/**
 * 채팅방 목록 페이지
 * - 내 채팅방 + 공개 채팅방 분리 표시
 * - 새 채팅 만들기 버튼은 생성 기능만
 * - 공개 채팅방은 클릭 시 즉시 참가
 * - 키보드 네비게이션, 접근성
 */

import { apiGetRooms, apiDiscoverRooms, apiCreateRoom, apiSearchUsers, apiJoinRoom } from '../lib/api';
import { escapeHtml, formatTime, getAvatarGradient } from '../lib/utils';
import { toast } from '../lib/toast';
import type { AppStateManager } from '../app';

interface RoomItem {
  id: string;
  name: string;
  created_by: string;
  member_count: number;
  last_message?: string;
  last_message_time?: number;
  has_password?: number;
}

let _app: AppStateManager;
let _selectedIndex = -1;
let _keyboardHandler: ((e: KeyboardEvent) => void) | null = null;
let _searchHandler: ((this: HTMLInputElement, e: Event) => void) | null = null;
let _createHandler: (() => void) | null = null;
let _searchTimeout: number | undefined;

export function renderRoomListPage(container: HTMLElement, app: AppStateManager): void {
  _app = app;
  _selectedIndex = -1;
  initSections();
  loadAllRooms();
  setupSearch();
  setupCreateButton();
  setupKeyboardNav();
}

function initSections(): void {
  const listEl = document.getElementById('room-list');
  if (!listEl) return;
    listEl.innerHTML = `
    <div id="section-joined"><div class="px-4 pt-4 pb-1 section-header">내 채팅방</div><div id="joined-list"></div></div>
    <div id="section-discover"><div class="px-4 pt-4 pb-1 section-header">공개 채팅방</div><div id="discover-list"></div></div>
  `;
}

async function loadAllRooms(): Promise<void> {
  showSkeleton('joined-list');
  showSkeleton('discover-list');

  try {
    const [myRes, pubRes] = await Promise.all([apiGetRooms(), apiDiscoverRooms()]);

    if (myRes.success && myRes.data) {
      renderRoomItems('joined-list', myRes.data, true);
    } else {
      document.getElementById('joined-list')!.innerHTML = '<p class="text-center py-8 text-gray-400 text-xs">채팅방을 불러올 수 없습니다.</p>';
    }

    if (pubRes.success && pubRes.data) {
      renderRoomItems('discover-list', pubRes.data, false);
    } else {
      document.getElementById('discover-list')!.innerHTML = '<p class="text-center py-8 text-gray-400 text-xs">공개 채팅방이 없습니다.</p>';
    }
  } catch {
    document.getElementById('joined-list')!.innerHTML = '<p class="text-center py-8 text-gray-400 text-xs">연결 오류</p>';
    document.getElementById('discover-list')!.innerHTML = '<p class="text-center py-8 text-gray-400 text-xs">연결 오류</p>';
  }
}

function showSkeleton(id: string): void {
  const el = document.getElementById(id);
  if (!el) return;
  el.innerHTML = Array(3).fill(0).map(() =>
    '<div class="flex items-center gap-3.5 px-4 py-3.5 bg-white rounded-xl skeleton h-[72px] mb-1.5"></div>'
  ).join('');
}

function renderRoomItems(containerId: string, rooms: RoomItem[], isJoined: boolean): void {
  const container = document.getElementById(containerId);
  if (!container) return;

  if (rooms.length === 0) {
    container.innerHTML = `<p class="text-center py-8 text-gray-400 text-xs">${isJoined ? '아직 참여 중인 채팅방이 없습니다' : '공개 채팅방이 없습니다'}</p>`;
    return;
  }

  container.innerHTML = rooms.map((room, index) => `
    <div class="room-item flex items-center gap-3.5 px-4 py-3.5 bg-white border-b border-border-light hover:bg-surface-secondary cursor-pointer transition-all duration-200 animate-fade-in"
         data-room-id="${room.id}"
         data-has-password="${room.has_password || 0}"
         data-joined="${isJoined ? '1' : '0'}"
         role="option"
         aria-label="${escapeHtml(room.name)}, ${room.member_count}명, ${isJoined ? '참여중' : '참가하기'}"
         tabindex="0">
      <div class="w-12 h-12 ${getAvatarGradient(room.name)} rounded-full flex items-center justify-center shrink-0 shadow-sm" aria-hidden="true">
        <span class="text-white font-bold text-base">${room.name.charAt(0).toUpperCase()}</span>
      </div>
      <div class="flex-1 min-w-0">
        <div class="flex items-center justify-between mb-0.5">
          <h3 class="font-semibold text-[15px] text-gray-900 truncate flex items-center gap-1">
            ${escapeHtml(room.name)}
            ${room.has_password ? '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="text-gray-400 shrink-0" aria-label="비밀번호 보호"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>' : ''}
          </h3>
          <span class="text-[11px] text-gray-400 shrink-0 ml-2 font-medium">${room.last_message_time ? formatTime(room.last_message_time) : ''}</span>
        </div>
        <div class="flex items-center justify-between">
          <p class="text-[13px] text-gray-500 truncate">${isJoined ? (room.last_message ? '암호화된 메시지' : '새로운 채팅방') : '탭하여 참가하기'}</p>
          <span class="text-[11px] text-kalpha-500 font-semibold ml-2 ${isJoined ? 'bg-kalpha-50' : 'bg-blue-50 text-blue-500'} px-2 py-0.5 rounded-full">${room.member_count}명${isJoined ? '' : ' 참가'}</span>
        </div>
      </div>
    </div>
  `).join('');

  bindRoomEvents(container, isJoined);
}

function bindRoomEvents(container: HTMLElement, isJoined: boolean): void {
  container.querySelectorAll('.room-item').forEach((item) => {
    item.addEventListener('click', async (e) => {
      e.preventDefault();
      const el = item as HTMLElement;
      const roomId = el.getAttribute('data-room-id')!;
      const hasPw = el.getAttribute('data-has-password') === '1';

      if (isJoined) {
        if (hasPw) showPasswordModal(roomId, true);
        else _app.navigateTo('chat', { roomId });
      } else {
        await joinRoom(roomId, hasPw);
      }
    });
  });
}

/** 공개 채팅방 참가 */
async function joinRoom(roomId: string, hasPassword: boolean): Promise<void> {
  if (hasPassword) {
    showPasswordModal(roomId, false);
    return;
  }

  try {
    const res = await apiJoinRoom(roomId);
    if (res.success) {
      toast('채팅방에 참가했습니다.', 'success');
      _app.navigateTo('chat', { roomId });
    } else {
      toast(res.error || '참가에 실패했습니다.', 'error');
    }
  } catch {
    toast('참가 중 오류가 발생했습니다.', 'error');
  }
}

function setupSearch(): void {
  const input = document.getElementById('room-search') as HTMLInputElement | null;
  if (!input) return;
  if (_searchHandler) input.removeEventListener('input', _searchHandler);
  _searchHandler = function(this: HTMLInputElement) {
    const q = this.value.toLowerCase().trim();
    let visibleCount = 0;
    document.querySelectorAll('.room-item').forEach((item) => {
      const name = item.querySelector('h3')?.textContent?.toLowerCase() || '';
      const show = name.includes(q);
      (item as HTMLElement).style.display = show ? '' : 'none';
      if (show) visibleCount++;
    });
    const listEl = document.getElementById('room-list');
    let emptyMsg = listEl?.querySelector('#search-empty-msg') as HTMLElement | null;
    if (q && visibleCount === 0) {
      if (!emptyMsg) {
        emptyMsg = document.createElement('p');
        emptyMsg.id = 'search-empty-msg';
        emptyMsg.className = 'text-center py-8 text-sm text-tertiary';
        listEl?.appendChild(emptyMsg);
      }
      emptyMsg.textContent = `"${q}"에 해당하는 채팅방이 없습니다`;
      emptyMsg.style.display = '';
    } else if (emptyMsg) {
      emptyMsg.style.display = 'none';
    }
  };
  input.addEventListener('input', _searchHandler);
}

function setupCreateButton(): void {
  const btn = document.getElementById('btn-create-room');
  if (!btn) return;
  if (_createHandler) btn.removeEventListener('click', _createHandler);
  _createHandler = () => showCreateRoomModal();
  btn.addEventListener('click', _createHandler);
}

function setupKeyboardNav(): void {
  if (_keyboardHandler) document.removeEventListener('keydown', _keyboardHandler);
  _keyboardHandler = (e: KeyboardEvent) => {
    if (_app.getCurrentPage() !== 'rooms') return;
    const items = document.querySelectorAll('.room-item');
    if (items.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); _selectedIndex = Math.min(_selectedIndex + 1, items.length - 1); focusItem(items); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); _selectedIndex = Math.max(_selectedIndex - 1, 0); focusItem(items); }
    else if (e.key === 'Enter' && _selectedIndex >= 0) {
      e.preventDefault();
      const el = items[_selectedIndex] as HTMLElement;
      el.click();
    }
  };
  document.addEventListener('keydown', _keyboardHandler);
}

function focusItem(items: NodeListOf<Element>): void {
  items.forEach((el, i) => {
    const h = el as HTMLElement;
    if (i === _selectedIndex) { h.classList.add('bg-kalpha-50','ring-2','ring-kalpha-300','ring-inset'); h.focus(); h.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }
    else h.classList.remove('bg-kalpha-50','ring-2','ring-kalpha-300','ring-inset');
  });
}

/** 비밀번호 입력 모달 */
function showPasswordModal(roomId: string, isAlreadyJoined: boolean): void {
  const overlay = document.getElementById('modal-overlay')!;
  const content = document.getElementById('modal-content')!;
  content.innerHTML = `
    <div class="flex items-center justify-between mb-5">
      <h2 class="text-lg font-bold text-gray-900">비밀번호 입력</h2>
      <button id="btn-close-modal" class="p-1.5 hover:bg-gray-100 rounded-full transition-colors" aria-label="닫기"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
    </div>
    <p class="text-sm text-gray-500 mb-4">이 채팅방은 비밀번호로 보호되어 있습니다.</p>
    <form id="password-form" class="space-y-4">
      <input id="room-password" type="password" required autofocus class="w-full px-4 py-3 bg-surface-secondary border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-kalpha-400/30 transition-all" placeholder="비밀번호를 입력하세요" aria-label="채팅방 비밀번호">
      <div id="password-error" class="text-red-500 text-sm hidden"></div>
      <button type="submit" class="w-full py-3 bg-kalpha-500 text-white rounded-xl text-sm font-semibold btn-primary">입장</button>
    </form>
  `;
  showModalOverlay();

  document.getElementById('password-form')!.addEventListener('submit', async (e) => {
    e.preventDefault();
    const pw = (document.getElementById('room-password') as HTMLInputElement).value;
    const errEl = document.getElementById('password-error')!;
    if (!pw) { errEl.textContent = '비밀번호를 입력해주세요.'; errEl.classList.remove('hidden'); return; }
    const token = localStorage.getItem('kalpha_token');
    try {
      const response = await fetch(`/api/rooms/${roomId}/join`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ password: pw }),
      });
      const json = await response.json();
      if (json.success) {
        closeModal();
        toast('채팅방에 입장했습니다.', 'success');
        _app.navigateTo('chat', { roomId });
      } else {
        errEl.textContent = json.error || '비밀번호가 틀렸습니다.';
        errEl.classList.remove('hidden');
      }
    } catch { errEl.textContent = '오류가 발생했습니다.'; errEl.classList.remove('hidden'); }
  });

  document.getElementById('btn-close-modal')!.addEventListener('click', closeModal);
  if (!overlay.dataset.hasClickListener) {
    overlay.addEventListener('click', (e) => { if (e.target === overlay) closeModal(); });
    overlay.dataset.hasClickListener = '1';
  }
}

/** 채팅방 생성 모달 */
function showCreateRoomModal(): void {
  const overlay = document.getElementById('modal-overlay')!;
  const content = document.getElementById('modal-content')!;
  content.innerHTML = `
    <div class="flex items-center justify-between mb-5">
      <h2 class="text-lg font-bold text-gray-900">새 채팅 만들기</h2>
      <button id="btn-close-modal" class="p-1.5 hover:bg-gray-100 rounded-full transition-colors" aria-label="닫기"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
    </div>
    <form id="create-room-form" class="space-y-4">
      <div>
        <label class="block text-sm font-semibold text-gray-700 mb-1.5" for="new-room-name">채팅방 이름</label>
        <input id="new-room-name" type="text" required class="w-full px-4 py-3 bg-surface-secondary border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-kalpha-400/30 transition-all" placeholder="채팅방 이름을 입력하세요">
      </div>
      <div>
        <label class="block text-sm font-semibold text-gray-700 mb-1.5">대화 상대 추가 (선택)</label>
        <div class="flex gap-2 mb-2">
          <input id="friend-id-input" type="text" class="flex-1 px-4 py-3 bg-surface-secondary border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-kalpha-400/30 transition-all" placeholder="아이디 입력 (@username)">
          <button type="button" id="btn-add-by-id" class="px-4 py-3 bg-surface-secondary text-kalpha-600 border border-border rounded-xl text-sm font-semibold hover:bg-kalpha-50 transition-colors shrink-0">추가</button>
        </div>
        <input id="user-search-input" type="text" class="w-full px-4 py-3 bg-surface-secondary border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-kalpha-400/30 transition-all" placeholder="또는 이름 검색 (2자 이상)">
        <div id="user-search-results" class="mt-2 max-h-36 overflow-y-auto space-y-1 rounded-xl"></div>
      </div>
      <div id="selected-users" class="flex flex-wrap gap-1.5"></div>
      <div>
        <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 cursor-pointer">
          <input id="enable-password" type="checkbox" class="w-4 h-4 rounded border-gray-300 text-kalpha-500 focus:ring-kalpha-400">
          비밀번호 보호 (비공개 채팅방)
        </label>
        <input id="room-password-input" type="password" disabled class="w-full mt-2 px-4 py-3 bg-surface-secondary border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-kalpha-400/30 transition-all hidden" placeholder="4자 이상의 비밀번호" minlength="4" maxlength="100">
      </div>
      <div id="create-room-error" class="text-red-500 text-sm hidden"></div>
      <div class="flex gap-2.5 pt-2">
        <button type="button" id="btn-cancel-create" class="flex-1 py-3 bg-gray-100 text-gray-600 rounded-xl text-sm font-semibold hover:bg-gray-200 transition-colors">취소</button>
        <button type="submit" class="flex-1 py-3 bg-kalpha-500 text-white rounded-xl text-sm font-semibold btn-primary shadow-sm">만들기</button>
      </div>
    </form>
  `;
  showModalOverlay();

  const selectedUsers: Array<{ id: string; display_name: string }> = [];

  document.getElementById('enable-password')!.addEventListener('change', function(this: HTMLInputElement) {
    const pwInput = document.getElementById('room-password-input')!;
    pwInput.classList.toggle('hidden', !this.checked);
    (pwInput as HTMLInputElement).disabled = !this.checked;
    if (this.checked) (pwInput as HTMLInputElement).focus();
  });

  document.getElementById('btn-add-by-id')!.addEventListener('click', async () => {
    const input = document.getElementById('friend-id-input') as HTMLInputElement;
    const username = input.value.trim();
    if (!username) return;
    const res = await apiSearchUsers(username);
    if (res.success && res.data) {
      const match = res.data.find(u => u.username.toLowerCase() === username.toLowerCase());
      if (match && !selectedUsers.find(s => s.id === match.id)) {
        selectedUsers.push({ id: match.id, display_name: match.display_name });
        updateSelectedUsers();
        input.value = '';
        toast(`@${match.username} 추가됨`, 'success');
      } else if (!match) toast('사용자를 찾을 수 없습니다.', 'error');
    }
  });

  const searchInput = document.getElementById('user-search-input') as HTMLInputElement;
  const searchResults = document.getElementById('user-search-results')!;
  let searchTimeout: number;
  searchInput.addEventListener('input', () => {
    clearTimeout(_searchTimeout);
    const query = searchInput.value.trim();
    if (query.length < 2) { searchResults.innerHTML = ''; return; }
    _searchTimeout = window.setTimeout(async () => {
      const res = await apiSearchUsers(query);
      if (res.success && res.data) {
        searchResults.innerHTML = res.data.filter(u => !selectedUsers.find(s => s.id === u.id)).map(u => `
          <div class="flex items-center justify-between px-3 py-2.5 hover:bg-kalpha-50 rounded-lg cursor-pointer user-result transition-colors" data-user-id="${u.id}" data-user-name="${escapeHtml(u.display_name)}" role="option" tabindex="0">
            <div class="flex items-center gap-2"><div class="w-8 h-8 ${getAvatarGradient(u.display_name)} rounded-full flex items-center justify-center" aria-hidden="true"><span class="text-white text-xs font-bold">${u.display_name.charAt(0)}</span></div><span class="text-sm font-medium">${escapeHtml(u.display_name)} <span class="text-xs text-gray-400">@${escapeHtml(u.username)}</span></span></div>
            <span class="text-xs text-kalpha-500 font-semibold bg-kalpha-50 px-2 py-1 rounded-full">+ 추가</span>
          </div>
        `).join('');
        searchResults.querySelectorAll('.user-result').forEach(el => {
          el.addEventListener('click', () => selectUser(el as HTMLElement));
          el.addEventListener('keydown', (ev) => { if ((ev as KeyboardEvent).key === 'Enter') selectUser(el as HTMLElement); });
        });
      }
    }, 300);
  });

  function selectUser(el: HTMLElement): void {
    const id = el.getAttribute('data-user-id')!, name = el.getAttribute('data-user-name')!;
    if (!selectedUsers.find(u => u.id === id)) { selectedUsers.push({ id, display_name: name }); updateSelectedUsers(); }
    searchResults.innerHTML = ''; searchInput.value = '';
  }

  function updateSelectedUsers(): void {
    const container = document.getElementById('selected-users')!;
    container.innerHTML = selectedUsers.map(u => `
      <span class="inline-flex items-center gap-1 px-3 py-1.5 bg-kalpha-50 text-kalpha-700 rounded-full text-sm font-medium border border-kalpha-100">${escapeHtml(u.display_name)}<button type="button" class="remove-user hover:text-red-500 ml-0.5 transition-colors" data-user-id="${u.id}" aria-label="${escapeHtml(u.display_name)} 제거"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button></span>
    `).join('');
    container.querySelectorAll('.remove-user').forEach(btn => { btn.addEventListener('click', () => { const idx = selectedUsers.findIndex(u => u.id === btn.getAttribute('data-user-id')!); if (idx >= 0) selectedUsers.splice(idx, 1); updateSelectedUsers(); }); });
  }

  document.getElementById('create-room-form')!.addEventListener('submit', async (e) => {
    e.preventDefault();
    const nameInput = document.getElementById('new-room-name') as HTMLInputElement;
    const errorEl = document.getElementById('create-room-error')!;
    const enablePw = (document.getElementById('enable-password') as HTMLInputElement).checked;
    const pw = (document.getElementById('room-password-input') as HTMLInputElement).value;
    if (!nameInput.value.trim()) { errorEl.textContent = '채팅방 이름을 입력해주세요.'; errorEl.classList.remove('hidden'); return; }
    if (enablePw && pw.length < 4) { errorEl.textContent = '비밀번호는 4자 이상이어야 합니다.'; errorEl.classList.remove('hidden'); return; }
    const res = await apiCreateRoom(nameInput.value.trim(), selectedUsers.map(u => u.id), enablePw ? pw : undefined);
    if (res.success && res.data) {
      closeModal();
      toast('채팅방이 생성되었습니다.', 'success');
      loadAllRooms();
    } else { errorEl.textContent = res.error || '생성에 실패했습니다.'; errorEl.classList.remove('hidden'); }
  });

  document.getElementById('btn-cancel-create')!.addEventListener('click', closeModal);
  document.getElementById('btn-close-modal')!.addEventListener('click', closeModal);
  if (!overlay.dataset.hasClickListener) {
    overlay.addEventListener('click', (e) => { if (e.target === overlay) closeModal(); });
    overlay.dataset.hasClickListener = '1';
  }
}

let _modalFocusReturn: HTMLElement | null = null;

function showModalOverlay(): void {
  const overlay = document.getElementById('modal-overlay')!, content = document.getElementById('modal-content')!;
  _modalFocusReturn = document.activeElement as HTMLElement;
  document.body.style.overflow = 'hidden';
  overlay.classList.remove('hidden');
  requestAnimationFrame(() => { overlay.classList.remove('opacity-0'); content.classList.remove('scale-95'); content.classList.add('scale-100'); });
  // 첫 번째 포커스 가능 요소로 이동
  setTimeout(() => {
    const focusable = content.querySelector<HTMLElement>('input, textarea, select, button, [href]');
    focusable?.focus();
  }, 50);
}

function closeModal(): void {
  const overlay = document.getElementById('modal-overlay')!, content = document.getElementById('modal-content')!;
  document.body.style.overflow = '';
  overlay.classList.add('opacity-0'); content.classList.add('scale-95'); content.classList.remove('scale-100');
  setTimeout(() => overlay.classList.add('hidden'), 300);
  if (_modalFocusReturn) { _modalFocusReturn.focus(); _modalFocusReturn = null; }
  clearTimeout(_searchTimeout);
}
